/** Strings of the "Estat" page and the placement report; merged into `ca` by src/i18n/ca.ts. */
export const ca_status = {
  statusPage: {
    title: 'Estat',
    intro:
      'Què s’ha vist funcionar en joc i què no: la nit de fum que la plataforma fa sola per comprovar-se, i el registre de verificació fila per fila, amb l’evidència que les proves guiades hi han deixat.',
    tabs: {
      smoke: 'Nit de fum',
      ledger: 'Registre',
      use: 'Vist en ús',
    },
    use: {
      help: 'La plataforma compta qui ha fet servir cada cosa de debò. No decideix res: ets tu qui ho dona per vist.',
      since: (version: string, date: string) => `Versió ${version}, en marxa des del ${date}.`,
      people: (n: number, version: string) =>
        n === 1
          ? `1 persona des de la versió ${version}`
          : `${n} persones des de la versió ${version}`,
      ever: (n: number) => (n === 1 ? '1 persona des de sempre' : `${n} persones des de sempre`),
      mine: 'tu també',
      mineOnly: 'només tu',
      ledgerRow: (id: string) => `Registre ${id}`,
      roadmapItem: 'Full de ruta',
      accept: 'Dona-ho per vist',
      accepted: (date: string) => `Vist en ús · ${date}`,
      acceptedCount: (n: number) =>
        n === 1 ? 'amb 1 persona comptada' : `amb ${n} persones comptades`,
      stale: (version: string) => `acceptat a la versió ${version}`,
      undo: 'Desfés',
      none: (n: number) => `Encara ningú (${n})`,
      empty: 'No hi ha res per comptar.',
      error: 'No s’ha pogut carregar el que s’ha vist en ús.',
    },
    error: 'No s’ha pogut carregar l’estat.',
    smoke: {
      intro:
        'La plataforma genera la missió mínima, engega el servidor de proves, hi posa un jugador de prova, el porta fins a l’objectiu i a l’extracció, i llegeix el registre del servidor fila per fila. Verd o vermell, amb les línies.',
      last: 'Última nit de fum',
      none: 'Encara no se n’ha fet cap.',
      running: 'En marxa',
      queued: (place: number | null | undefined, why: string | null | undefined) =>
        `lloc ${place ?? '?'} a la cua${why ? ` (${why})` : ''}`,
      phase: (p: string) =>
        (
          ({
            QUEUED: 'a la cua',
            GENERATING: 'generant la missió',
            STARTING: 'engegant el servidor',
            WAITING_RUNTIME: 'esperant que el runtime informi',
            HEADLESS: 'esperant el client headless',
            PROXY: 'posant el jugador de prova',
            APPROACH: 'el jugador s’acosta a l’objectiu',
            RESOLVE: 'completant l’objectiu',
            EXTRACT: 'anant a l’extracció',
            ENDING: 'acabant la missió',
            DONE: 'acabada',
          }) as Record<string, string>
        )[p] ?? p,
      result: (r: string) =>
        (
          ({
            QUEUED: 'A la cua',
            RUNNING: 'En marxa',
            GREEN: 'Verd',
            RED: 'Vermell',
            ABORTED: 'Interrompuda',
          }) as Record<string, string>
        )[r] ?? r,
      checks: 'Comprovacions',
      check: (s: string) =>
        s === 'pass' ? 'bé' : s === 'fail' ? 'falla' : s === 'warn' ? 'avís' : 'no aplica',
      evidence: 'Línies',
      session: 'Sessió',
      version: (platform: string | null, addon: string | null) =>
        `plataforma ${platform ?? '?'} · runtime ${addon ?? '?'}`,
      candidate: (folder: string) => `candidat ${folder}`,
      commit: (sha: string) => `commit ${sha.slice(0, 10)}`,
      note: 'Nota',
      history: 'Nits anteriors',
      start: 'Fes una nit de fum',
      starting: 'Començant…',
      started: 'La nit de fum ha començat; aquesta pàgina s’actualitza sola.',
      abort: 'Interromp',
      form: {
        server: 'Servidor de la plataforma',
        world: 'Terreny (classe del món)',
        candidate: 'Carpeta candidata (opcional, p. ex. @ops_rc)',
        commit: 'Commit provat (opcional)',
        mission: 'Missió desada (opcional: codi, o codi@revisió)',
        missionHint:
          'Juga una còpia d’aquesta missió tal com està desada, sense generar-ne cap: el terreny i el tipus són els seus. Serveix per provar una missió editada o el banc de proves.',
        missionBad: 'La revisió va després de @ i és un nombre: k7f2m9q@3.',
        noServer: 'Cap servidor gestionat per la plataforma; crea’n un a Servidors.',
      },
      errors: {
        smoke_running: 'Ja hi ha una nit de fum en marxa.',
        smoke_server_busy:
          'Aquest servidor ja té una nit que l’ha de fer servir (la de la porta de publicació o una amb client de fora).',
        server_not_managed: 'Aquest servidor no és de la plataforma.',
      } as Record<string, string>,
    },
    ledger: {
      intro:
        'Una fila per capacitat, amb com es verifica (UNIT prova automàtica, AUTO la nit de fum, HUMAN una persona, OPS l’operador) i què s’ha vist. Una fila només canvia amb evidència; «vist per persones» vol dir que s’ha vist funcionar jugant i només hi ha la paraula del propietari, sense codi de sessió.',
      builtOn: (d: string) => `Registre del ${d}`,
      states: {
        seen: 'vist',
        people: 'vist per persones',
        failing: 'falla',
        unverified: 'no verificat',
        frozen: 'congelat',
      } as Record<string, string>,
      all: 'Tot',
      columns: { id: 'Id', row: 'Capacitat', check: 'Com', status: 'Estat' },
    },
  },
  placement: {
    title: 'Col·locació',
    intro:
      'Cada objecte de la base, cada plaça, cada vehicle, cada força i cada punt: on es demanava, on és, què té a sota. Mig metre és el límit.',
    columns: {
      kind: 'Què',
      id: 'Id',
      class: 'Classe',
      placed: 'On és',
      under: 'A sota',
      gap: 'Diferència',
      outcome: 'Resultat',
      note: 'Nota',
    },
    kinds: {
      object: 'objecte',
      slot: 'plaça',
      vehicle: 'vehicle',
      force: 'força',
      point: 'punt',
      objective: 'objectiu',
      intel: 'informació',
    } as Record<string, string>,
    outcomes: {
      ok: 'bé',
      hung: 'penjat a una paret',
      resting: 'sobre una altra peça',
      floating: 'flota',
      sunk: 'enfonsat',
      water: 'a l’aigua',
      destroyed: 'destruït',
      moved: 'mogut',
      clipping: 'travessa una paret',
      bare: 'a terra, sense taula',
      open: 'a la taula, sense sostre',
    } as Record<string, string>,
    // What holds a piece up (the row's `on`): shown after the note when it is a thing with a name.
    stances: {
      floor: 'a terra de',
      piece: 'sobre',
      wall: 'penjat a',
      authored: 'a l’aire, com ho volia l’autor, sobre',
    } as Record<string, string>,
    summary: (total: number, off: number, water: number) =>
      `${total} files · ${off} fora de lloc · ${water} a l’aigua`,
    onlyProblems: 'Només els problemes',
    none: 'El runtime encara no ha enviat l’informe de col·locació.',
  },
};
